import { addToDataLayer } from '@/utils/analytics/gtm';
import { logger } from '@/utils';
import {
  GtmEcommerceEventPayload,
  GtmEcommerceItem,
  GtmEcommerceData,
  GtmEcommerceParams,
  GTM_ECOMMERCE_EVENTS,
  GTM_INTERVAL_UNIT,
  GTM_ITEM_BRAND,
  GTM_ITEM_CATEGORY,
  GtmEcommerceEventName,
  GtmIntervalUnit,
  GtmAddress,
  GtmItemCategory,
} from './gtm-ecommerce.types';
import {
  IPlan,
  EPlanIntervalUnit,
  EPlanIntervalCount,
} from '@/models/checkout.model';

/**
 * Maps plan interval_unit and interval_count to GTM interval unit format
 */
export const mapIntervalUnit = (
  intervalUnit: string,
  intervalCount: number
): GtmIntervalUnit => {
  if (
    intervalUnit === EPlanIntervalUnit.MONTH &&
    intervalCount === EPlanIntervalCount.SIX
  ) {
    return GTM_INTERVAL_UNIT.SIX_MONTH;
  }

  switch (intervalUnit) {
    case EPlanIntervalUnit.YEAR:
      return GTM_INTERVAL_UNIT.YEAR;
    case EPlanIntervalUnit.MONTH:
      return GTM_INTERVAL_UNIT.MONTH;
    case EPlanIntervalUnit.LIFETIME:
      return GTM_INTERVAL_UNIT.LIFETIME;
    default:
      return GTM_INTERVAL_UNIT.MONTH;
  }
};

/**
 * Builds the ecommerce item object for GTM
 */
const buildEcommerceItem = (
  plan: IPlan,
  itemCategory: GtmItemCategory,
  couponCode?: string,
  discountAmount?: number
): GtmEcommerceItem => {
  const item: GtmEcommerceItem = {
    item_name: plan.plan_name,
    item_brand: GTM_ITEM_BRAND,
    item_category: itemCategory,
    price: plan.price,
    quantity: 1,
    ...(couponCode && { coupon: couponCode }),
    ...(discountAmount && { discount: discountAmount }),
  };

  return item;
};

/**
 * Builds the ecommerce data object for GTM
 */
const buildEcommerceData = (
  plan: IPlan,
  itemCategory: GtmItemCategory,
  couponCode?: string,
  discountAmount?: number
): GtmEcommerceData => {
  const ecommerce: GtmEcommerceData = {
    currency: plan.currency,
    value: plan.price,
    ...(couponCode && { coupon: couponCode }),
    ...(discountAmount && { discount: discountAmount }),
    items: [buildEcommerceItem(plan, itemCategory, couponCode, discountAmount)],
  };

  return ecommerce;
};

/**
 * Builds the address object for GTM (only if at least one field exists)
 */
const buildAddress = (
  firstName?: string,
  lastName?: string
): GtmAddress | undefined => {
  if (!firstName && !lastName) {
    return;
  }

  return {
    ...(firstName && { first_name: firstName }),
    ...(lastName && { last_name: lastName }),
  };
};

/**
 * Builds the complete GTM ecommerce event payload
 */
export const buildGtmEcommercePayload = (
  eventName: GtmEcommerceEventName,
  params: GtmEcommerceParams
): GtmEcommerceEventPayload => {
  const {
    plan,
    userId,
    status,
    purchaseBehavior,
    itemCategory = GTM_ITEM_CATEGORY.SUBSCRIPTION,
    paymentType,
    email,
    phoneNumber,
    firstName,
    lastName,
    couponCode,
    discountAmount,
  } = params;

  const address = buildAddress(firstName, lastName);
  const hasTrialPeriod = plan.trial_period_in_days > 0;
  const planProvider = paymentType
    ? plan.providers?.find(p => p.provider === paymentType.toUpperCase())
    : undefined;

  const providers = paymentType
    ? plan.providers?.filter(p => p.provider === paymentType.toUpperCase())
    : plan.providers;

  const planIds = providers?.map(p => p.planId) ?? [];
  const productIds = providers?.map(p => p.productId) ?? [];

  const payload: GtmEcommerceEventPayload = {
    event: eventName,
    user_id: userId,
    plan_name: plan.plan_name,
    price_per_month: plan.price_per_month?.toFixed(2),
    price_before_discount: (plan.price_before_discount ?? plan.price)?.toFixed(
      2
    ),
    ...(hasTrialPeriod && {
      trial_period_in_days: plan.trial_period_in_days,
    }),
    purchase_behavior: purchaseBehavior,
    status,
    interval_count: plan.interval_count,
    interval_unit: mapIntervalUnit(plan.interval_unit, plan.interval_count),
    ...(paymentType && { payment_type: paymentType }),
    ...(email && { email_address: email }),
    ...(phoneNumber && { phone_number: phoneNumber }),
    ...(address && { address }),
    ecommerce: buildEcommerceData(
      plan,
      itemCategory,
      couponCode,
      discountAmount
    ),
  };

  return payload;
};
